import {
  Button,
  ButtonSizeMD,
  ButtonSizeSM,
  ButtonSkinMarket,
  ButtonSkinSecondary,
  ButtonVariants,
} from '@picsart/button';
import cls from 'classnames';
import { clipboardWrite } from '@quicktools/shared-components/utils/text/clipboard';
import IconRefresh from '@picsart/icons/IconRefresh';
import IconEdit from '@picsart/icons/IconEdit';
import { useTranslation } from 'react-i18next';
import { useRef, MouseEvent, useCallback } from 'react';
import { ScreenSizes } from '@picsart/cascade';

import { useEditorStore } from '../../../services/store/playground-editor.store';
import { getCategoryName, allowRephrasing } from '../../../utils/common/category';
import analytics from '../../../services/analytics';
import WordCount from '../WordCount';
import ResultTextInputArea from '../../formInputs/ResultTextInputArea/ResultTextInputArea';
import Rephrased from '../../EditorPage/rephraseTextSection/Rephrased';
import CopyButton from '../CopyButton';
import { ToolCategory, IRephrasedResult, ResultType } from '../../../utils/common/types';
import { getTextCategoryByType } from '../../../utils/common/analytics';
import useStore from '../../../services/store';

import useStyles from './styles';

type AiWriterResultProps = {
  id: string;
  index: number;
  rephrase?: (
    id: string,
    textToRephrase: string,
    showWordCount: boolean,
    category: ToolCategory,
  ) => void;
  showWordCount: boolean;
  canBeSavedToRecent?: boolean;
  category: ToolCategory;
  markdown: string;
  rephrasedResults?: IRephrasedResult[];
  isRephrasing?: boolean;
  type: ResultType;
};

function AiWriterResult({
  id,
  index,
  category,
  showWordCount,
  rephrase,
  canBeSavedToRecent,
  markdown,
  rephrasedResults,
  isRephrasing,
  type,
}: AiWriterResultProps) {
  const classes = useStyles();
  const { t } = useTranslation();
  const hasRephrasedResults = rephrasedResults?.length > 0;
  const showRephrased = isRephrasing || hasRephrasedResults;
  const numberOfWords = markdown?.match(/\S+/g)?.length ?? 0;
  const numberOfCharacters = markdown?.length;
  const ref = useRef<HTMLDivElement>(null);
  const addRecents = useStore((state) => state.addRecents);
  const copyTextToEditor = useEditorStore((state) => state.pasteText);
  const openEditor = useEditorStore((state) => state.open);

  const handleCopyText = useCallback(
    (name: string) => {
      analytics.copy({
        page_path: window.location.pathname,
        action_type: name,
        category_name: getCategoryName(category),
        copied_text: { index: `${index + 1}`, text: markdown },
        text_category: getTextCategoryByType(type),
      });

      clipboardWrite(ref.current.textContent, ref.current.innerHTML);
      canBeSavedToRecent && addRecents({ category, id });
    },
    [addRecents, canBeSavedToRecent, category, id, index, markdown, type],
  );

  const handleClick = useCallback(
    (e) => {
      e.stopPropagation();
      analytics.textBoxClick({
        text_box_name: `result_box_${index + 1}`,
        category_name: getCategoryName(category),
        page_path: window.location.pathname,
      });
    },
    [category, index],
  );

  const handleRephraseText = () => {
    rephrase && rephrase(id, markdown, showWordCount, category);
  };

  const onEditClick = (e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    copyTextToEditor(markdown);
    openEditor();
  };

  return (
    <div className={classes.root} id={id}>
      <div className={cls(classes.container, `Result-${type}`, classes.editable)}>
        <ResultTextInputArea
          isEditable={false}
          index={index}
          markdown={markdown}
          handleClick={handleClick}
          onInput={() => null}
          ref={ref}
          hasMaxHeight={type === ResultType.Recent}
        />

        <div className={classes.buttonsAndCount}>
          <WordCount
            showWordCount={showWordCount}
            numberOfCharacters={numberOfCharacters}
            numberOfWords={numberOfWords}
          />

          <div className={classes.buttons}>
            {rephrase && allowRephrasing(category) && (
              <Button
                className={classes.rephraseButton}
                isDisabled={hasRephrasedResults || isRephrasing}
                onClick={handleRephraseText}
                variant={ButtonVariants.Text}
                startIcon={IconRefresh}
                skin={ButtonSkinSecondary}
                size={{
                  [ScreenSizes.MD]: ButtonSizeMD,
                  [ScreenSizes.SM]: ButtonSizeSM,
                }}
              >
                <span className="mobile">{t('editor.results.rephraseButtonMobile')}</span>
                <span className="desktop">{t('editor.results.rephraseButton')}</span>
              </Button>
            )}
            <Button
              size={{
                [ScreenSizes.MD]: ButtonSizeMD,
                [ScreenSizes.SM]: ButtonSizeSM,
              }}
              onClick={onEditClick}
              variant={ButtonVariants.Text}
              startIcon={IconEdit}
              skin={ButtonSkinSecondary}
            >
              {t('editor.results.edit')}
            </Button>
            <CopyButton
              size={{
                [ScreenSizes.MD]: ButtonSizeMD,
                [ScreenSizes.SM]: ButtonSizeSM,
              }}
              skin={ButtonSkinMarket}
              onClick={() => handleCopyText('copy_button')}
            />
          </div>
        </div>
      </div>
      {showRephrased && (
        <Rephrased
          canBeSavedToRecents={canBeSavedToRecent}
          category={category}
          showWordCount={showWordCount}
          originalTextIndex={index}
          rephrasedResults={rephrasedResults}
          isLoading={isRephrasing}
        />
      )}
    </div>
  );
}
export default AiWriterResult;
